import type { SettingsRepository } from "@/data/repositories";
import type { Locale } from "@/domain/localized";

/** The platform's per-app language. `launched` is undefined where the platform has none. */
export interface LanguagePort {
  /** The app's own language when this run started; null means the device language. */
  launched(): Promise<string | null | undefined>;
  set(language: string | null): Promise<void>;
}

/**
 * Keeps the system's per-app language equal to the language chosen in u-memo, so the
 * controls the system draws (iOS date and time pickers) speak it too. The system reads
 * it at launch: a change applies after a restart, which Settings tells the user.
 */
export class SystemLanguage {
  constructor(private readonly port: LanguagePort | null) {}

  start(settings: SettingsRepository): () => void {
    if (!this.port) return () => undefined;
    const port = this.port;
    const apply = () => void settings.get().then((s) => port.set(s.locale ?? null)).catch(() => undefined);
    apply();
    return settings.changes.subscribe(apply);
  }

  async needsRestart(locale: Locale | undefined): Promise<boolean> {
    if (!this.port) return false;
    const launched = await this.port.launched().catch(() => undefined);
    if (launched === undefined) return false;
    return launched !== (locale ?? null);
  }
}

interface AppLanguagePlugin {
  launched(): Promise<{ language: string | null }>;
  set(options: { language: string | null }): Promise<void>;
}

/** iOS only: the AppLanguage plugin registered in AppDelegate.swift; elsewhere there is no such setting. */
export class CapacitorLanguagePort implements LanguagePort {
  /** Registered once. A Capacitor plugin proxy answers any property, `then` included, so it never travels through a promise. */
  private plugin: AppLanguagePlugin | null | undefined;

  private async load(): Promise<void> {
    if (this.plugin !== undefined) return;
    const { Capacitor, registerPlugin } = await import("@capacitor/core");
    this.plugin = Capacitor.getPlatform() === "ios" ? registerPlugin<AppLanguagePlugin>("AppLanguage") : null;
  }

  async launched(): Promise<string | null | undefined> {
    await this.load();
    return this.plugin ? (await this.plugin.launched()).language : undefined;
  }

  async set(language: string | null): Promise<void> {
    await this.load();
    await this.plugin?.set({ language });
  }
}
